(() => {
  const ROOT_SELECTOR = '[data-native-addons]';
  const WATTAGES = [2000, 3000, 4000, 5000, 6000];
  const normalise = (value) => String(value || '').trim().toLowerCase();
  const containsAny = (value, terms) => terms.some((term) => normalise(value).includes(term));

  const readConfig = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-native-addons-state]')?.textContent || '{}');
    } catch (error) {
      console.warn('[Native add-ons] Konfiguration konnte nicht gelesen werden.', error);
      return {};
    }
  };

  const getWattage = (optionNames, optionValues) => {
    const namedIndex = optionNames.findIndex((name) => containsAny(name, ['leistung', 'power', 'watt']));
    const candidates = namedIndex >= 0 ? [optionValues[namedIndex], ...optionValues] : optionValues;
    const text = normalise(candidates.join(' '));
    return WATTAGES.find((wattage) => new RegExp(`(^|\\D)${wattage}(\\D|$)`).test(text)) || 0;
  };

  const getInstallation = (optionNames, optionValues) => {
    const namedIndex = optionNames.findIndex((name) => containsAny(name, ['montage', 'halter', 'installation']));
    const rawValue = namedIndex >= 0 ? optionValues[namedIndex] : optionValues.find((value) => containsAny(value, ['ohne halter', 'ohne montage', 'flachdach', 'ziegel', 'fs', 'zs', 'bs']));
    const value = normalise(rawValue);
    if (!value) return { key: '', hasMount: false, noMount: false };
    if (containsAny(value, ['ohne', 'kein', 'none'])) return { key: 'none', hasMount: false, noMount: true };
    if (/(^|\W)fs(\W|$)/.test(value) || value.includes('flachdach')) return { key: 'fs', hasMount: true, noMount: false };
    if (/(^|\W)zs(\W|$)/.test(value) || value.includes('ziegel')) return { key: 'zs', hasMount: true, noMount: false };
    if (/(^|\W)bs(\W|$)/.test(value) || value.includes('balkon')) return { key: 'bs', hasMount: true, noMount: false };
    return { key: value, hasMount: true, noMount: false };
  };

  const getModuleCount = (optionNames, optionValues) => {
    const namedIndex = optionNames.findIndex((name) => containsAny(name, ['modul', 'panel', 'anzahl']));
    if (namedIndex < 0) return 0;
    const match = normalise(optionValues[namedIndex]).match(/\d+/);
    return match ? Number(match[0]) : 0;
  };

  const getCountryKey = (value) => {
    const country = normalise(value);
    if (['at', 'austria', 'österreich', 'oesterreich'].includes(country)) return 'at';
    if (['de', 'germany', 'deutschland'].includes(country)) return 'de';
    return country;
  };

  const getDeliveryCountry = (optionNames, optionValues, fallbackCountry) => {
    const namedIndex = optionNames.findIndex((name) => containsAny(name, ['lieferland', 'delivery country', 'land der lieferung']));
    const selectedCountry = namedIndex >= 0 ? optionValues[namedIndex] : fallbackCountry;
    return getCountryKey(selectedCountry);
  };

  const getVariantState = (root, variant) => {
    const config = root.nativeAddonsConfig || {};
    const optionNames = Array.isArray(config.optionNames) ? config.optionNames.map(normalise) : [];
    const optionValues = Array.isArray(variant?.options) ? variant.options : (Array.isArray(config.variant?.options) ? config.variant.options : []);
    const selectedText = normalise(optionValues.join(' '));
    return {
      wattage: getWattage(optionNames, optionValues),
      modules: getModuleCount(optionNames, optionValues),
      hasBP4: containsAny(selectedText, ['bp4', 'bp5000']) && !containsAny(selectedText, ['ohne batterie', 'ohne speicher']),
      installation: getInstallation(optionNames, optionValues),
      country: getDeliveryCountry(optionNames, optionValues, config.country),
    };
  };

  const tagValues = (card, prefix) => normalise(card.dataset.addonTags).split('|').filter((tag) => tag.startsWith(prefix)).map((tag) => tag.slice(prefix.length));

  const isCompatible = (card, state) => {
    const tags = normalise(card.dataset.addonTags);
    if (tags.split('|').includes('addon:hidden')) return false;
    const addonCountry = getCountryKey(card.dataset.addonCountry);
    if (addonCountry && state.country && addonCountry !== state.country) return false;
    const wattages = tagValues(card, 'addon:watt:').map(Number).filter(Boolean);
    if (wattages.length && state.wattage && !wattages.includes(state.wattage)) return false;
    const moduleCounts = tagValues(card, 'addon:modules:').map(Number).filter(Boolean);
    if (moduleCounts.length && state.modules && !moduleCounts.includes(state.modules)) return false;
    const installations = tagValues(card, 'addon:installation:');
    if (installations.length && state.installation.key && !installations.includes(state.installation.key)) return false;
    const batteries = tagValues(card, 'addon:battery:');
    if (batteries.length && !batteries.includes(state.hasBP4 ? 'bp4' : 'none')) return false;

    const role = card.dataset.addonRole;
    if (role === 'bp3' && state.hasBP4) return false;
    if (role === 'bp4' && state.hasBP4 && state.wattage > 0 && state.wattage <= 4000) return false;
    if (role === 'mount' && state.installation.hasMount) return false;
    return true;
  };

  const transferCountrySelections = (root, state) => {
    if (!state.country) return;

    root.querySelectorAll('[data-native-addon-card]').forEach((card) => {
      const checkbox = card.querySelector('[data-native-addon-checkbox]');
      const cardCountry = getCountryKey(card.dataset.addonCountry);
      if (!checkbox?.checked || !cardCountry || cardCountry === state.country) return;

      const replacement = [...root.querySelectorAll('[data-native-addon-card]')].find((candidate) => (
        candidate !== card &&
        candidate.dataset.productId === card.dataset.productId &&
        candidate.dataset.addonConfiguration === card.dataset.addonConfiguration &&
        getCountryKey(candidate.dataset.addonCountry) === state.country
      ));
      const replacementCheckbox = replacement?.querySelector('[data-native-addon-checkbox]');
      const quantity = checkbox.dataset.quantity || '1';

      checkbox.checked = false;
      syncCardState(card);

      if (replacementCheckbox && !replacementCheckbox.disabled) {
        replacementCheckbox.checked = true;
        setQuantity(replacement, quantity);
        syncCardState(replacement);
      }
    });
  };

  const getPriority = (role, state) => {
    if (state.hasBP4) {
      if (state.wattage >= 5000) return ({ bp4: 10, cable: 20, 'smart-plug': 30, mount: 40, other: 50 })[role] || 60;
      return ({ cable: 10, 'smart-plug': 20, mount: 30, other: 40, bp4: 50, bp3: 60 })[role] || 70;
    }
    if (state.wattage === 2000) return ({ bp4: 10, bp3: 20, cable: 30, 'smart-plug': 40, mount: 50, other: 60 })[role] || 70;
    if (state.wattage === 3000) return ({ bp4: 10, bp3: 15, cable: 20, 'smart-plug': 30, mount: 40, other: 50 })[role] || 60;
    return ({ bp4: 10, cable: 20, bp3: 30, 'smart-plug': 40, mount: 50, other: 60 })[role] || 70;
  };

  const setQuantity = (card, nextValue) => {
    const checkbox = card.querySelector('[data-native-addon-checkbox]');
    const input = card.querySelector('[data-native-addon-quantity]');
    if (!checkbox || !input) return;
    const min = Number.parseInt(input.min, 10) || 1;
    const max = Number.parseInt(input.max, 10) || 10;
    const quantity = Math.min(max, Math.max(min, Number.parseInt(nextValue, 10) || min));
    input.value = String(quantity);
    checkbox.dataset.quantity = String(quantity);
  };

  const syncCardState = (card) => {
    const checkbox = card?.querySelector('[data-native-addon-checkbox]');
    if (!checkbox) return;
    card.classList.toggle('is-selected', checkbox.checked);
    card.querySelectorAll('[data-native-addon-minus], [data-native-addon-plus], [data-native-addon-quantity]').forEach((control) => { control.disabled = !checkbox.checked; });
  };

  const preventDuplicateVariant = (root, currentCheckbox) => {
    if (!currentCheckbox.checked) return;
    document.querySelectorAll('input[name="bundles"]:checked').forEach((checkbox) => {
      if (checkbox === currentCheckbox || checkbox.value !== currentCheckbox.value) return;
      checkbox.checked = false;
      syncCardState(checkbox.closest('[data-native-addon-card]'));
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const formatAddedPrice = (cents) => `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100)} €`;
  const updateSummary = (root) => {
    const summary = root.querySelector('[data-native-addons-summary]');
    const price = root.querySelector('[data-native-addons-summary-price]');
    if (!summary || !price) return;
    const selected = [...root.querySelectorAll('[data-native-addon-checkbox]:checked')];
    const total = selected.reduce((sum, checkbox) => sum + ((Number.parseInt(checkbox.dataset.price, 10) || 0) * Math.max(1, Number.parseInt(checkbox.dataset.quantity, 10) || 1)), 0);
    summary.hidden = selected.length === 0;
    price.textContent = formatAddedPrice(total);
  };

  const applyRules = (root, variant) => {
    const state = getVariantState(root, variant);
    if (typeof root.nativeProductSetHasBP4 === 'boolean') state.hasBP4 = root.nativeProductSetHasBP4;
    transferCountrySelections(root, state);
    root.querySelectorAll('[data-native-addon-card]').forEach((card, index) => {
      if (!card.dataset.nativeOriginalOrder) card.dataset.nativeOriginalOrder = String(index + 1);
      const compatible = isCompatible(card, state);
      card.hidden = !compatible;
      card.classList.toggle('is-rule-hidden', !compatible);
      const checkbox = card.querySelector('[data-native-addon-checkbox]');
      if (!compatible && checkbox?.checked) {
        checkbox.checked = false;
        syncCardState(card);
      }
      card.classList.remove('is-recommended');
    });
    root.querySelectorAll('[data-native-addon-group]').forEach((group) => {
      const list = group.querySelector('.product-native-addons__list');
      if (!list) return;
      [...list.querySelectorAll('[data-native-addon-card]')].sort((a, b) => getPriority(a.dataset.addonRole, state) - getPriority(b.dataset.addonRole, state) || Number(a.dataset.nativeOriginalOrder) - Number(b.dataset.nativeOriginalOrder)).forEach((card) => list.appendChild(card));
      group.hidden = !list.querySelector('[data-native-addon-card]:not([hidden])');
    });
    const installationHelp = root.querySelector('[data-native-installation-help]');
    if (installationHelp) installationHelp.hidden = !state.installation.noMount;
    updateSummary(root);
    root.dispatchEvent(new CustomEvent('native-addons:rules-applied', { bubbles: true, detail: { state } }));
  };

  const initRoot = (root) => {
    if (root.dataset.nativeAddonsReady === 'true') return;
    root.dataset.nativeAddonsReady = 'true';
    root.nativeAddonsConfig = readConfig(root);
    root.querySelectorAll('[data-native-addon-card]').forEach(syncCardState);
    applyRules(root, root.nativeAddonsConfig.variant);

    root.addEventListener('change', (event) => {
      const card = event.target.closest?.('[data-native-addon-card]');
      if (!card) return;
      if (event.target.matches('[data-native-addon-checkbox]')) {
        preventDuplicateVariant(root, event.target);
        syncCardState(card);
      }
      if (event.target.matches('[data-native-addon-quantity]')) setQuantity(card, event.target.value);
      updateSummary(root);
    });
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-native-addon-minus], [data-native-addon-plus]');
      if (!button) return;
      const card = button.closest('[data-native-addon-card]');
      const input = card?.querySelector('[data-native-addon-quantity]');
      if (!card || !input) return;
      setQuantity(card, (Number.parseInt(input.value, 10) || 1) + (button.matches('[data-native-addon-plus]') ? 1 : -1));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const eventScope = root.closest('product-info') || document;
    eventScope.addEventListener('variant:change', (event) => {
      root.nativeAddonsConfig.variant = event.detail?.variant || root.nativeAddonsConfig.variant;
      root.nativeProductSetHasBP4 = undefined;
      applyRules(root, event.detail?.variant);
    });
    eventScope.addEventListener('product-set:change', (event) => {
      const selectedText = normalise((event.detail?.selections || []).map(({ item }) => item?.title).join(' '));
      root.nativeProductSetHasBP4 = containsAny(selectedText, ['bp4', 'bp5000']) || undefined;
      applyRules(root, root.nativeAddonsConfig.variant);
    });
    document.addEventListener('change', (event) => {
      const externalBundle = event.target.closest?.('input[name="bundles"]:checked');
      if (!externalBundle || externalBundle.matches('[data-native-addon-checkbox]')) return;
      root.querySelectorAll('[data-native-addon-checkbox]:checked').forEach((checkbox) => {
        if (checkbox.value !== externalBundle.value) return;
        checkbox.checked = false;
        syncCardState(checkbox.closest('[data-native-addon-card]'));
      });
      updateSummary(root);
    });
  };

  const init = (scope = document) => scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
